/**
 * Invite Team Step Component
 * TASK-339: Onboarding Wizard UI
 *
 * Step 4: Invite team members (optional)
 */

import React, { useState } from 'react';
import { Loader2, Plus, X, Users, Mail, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { adminOnboardingService } from '../../../services/adminOnboardingService';
import { TeamInvitation } from '../../../types/adminOnboarding';

interface InviteTeamStepProps {
  token: string;
  companyName: string;
  onComplete: () => void;
  onSkip: () => void;
  isLoading?: boolean;
}

const InviteTeamStep: React.FC<InviteTeamStepProps> = ({
  token,
  companyName,
  onComplete,
  onSkip,
  isLoading: parentLoading = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([
    { email: '', first_name: '', last_name: '', role: 'advisor' },
  ]);
  const [sentResult, setSentResult] = useState<{ sent: number; failed: string[] } | null>(null);

  // Add new invitation row
  const addInvitation = () => {
    if (invitations.length < 10) {
      setInvitations([
        ...invitations,
        { email: '', first_name: '', last_name: '', role: 'advisor' },
      ]);
    }
  };

  // Remove invitation row
  const removeInvitation = (index: number) => {
    if (invitations.length > 1) {
      setInvitations(invitations.filter((_, i) => i !== index));
    }
  };

  // Update invitation field
  const updateInvitation = (index: number, field: keyof TeamInvitation, value: string) => {
    setInvitations(
      invitations.map((inv, i) =>
        i === index ? { ...inv, [field]: value } : inv
      )
    );
  };

  // Validate email
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Get valid invitations
  const getValidInvitations = () => {
    return invitations.filter(inv => inv.email && isValidEmail(inv.email));
  };

  const handleSendInvitations = async () => {
    const validInvitations = getValidInvitations();

    if (validInvitations.length === 0) {
      toast.error('Please enter at least one valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const response = await adminOnboardingService.sendTeamInvitations({
        token,
        invitations: validInvitations,
      });

      setSentResult({
        sent: response.sent_count,
        failed: response.failed,
      });

      if (response.sent_count > 0) {
        toast.success(`Sent ${response.sent_count} invitation(s)!`);
      }
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to send invitations';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = () => {
    onComplete();
  };

  // If invitations were sent, show result
  if (sentResult) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle size={32} className="text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Invitations Sent!
        </h3>
        <p className="text-gray-600 mb-6">
          {sentResult.sent} invitation{sentResult.sent !== 1 ? 's' : ''} sent successfully.
        </p>

        {sentResult.failed.length > 0 && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
            <div className="flex items-start gap-2">
              <AlertCircle size={18} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Some invitations failed:</p>
                <ul className="mt-2 text-sm text-yellow-700 space-y-1">
                  {sentResult.failed.map((email, idx) => (
                    <li key={idx}>{email}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={handleComplete}
          disabled={parentLoading}
          className="w-full py-3 px-4 bg-kielis-navy text-white font-medium rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {parentLoading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Completing Setup...
            </>
          ) : (
            'Complete Setup'
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3">
        <Users size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Invite your team to {companyName}</p>
          <p className="mt-1 text-blue-700">
            Team members will receive an email invitation to join your account.
            You can also add more users later from the Admin Panel.
          </p>
        </div>
      </div>

      {/* Invitation rows */}
      <div className="space-y-4">
        {invitations.map((invitation, index) => (
          <div key={index} className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">
                Team Member {index + 1}
              </span>
              {invitations.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeInvitation(index)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <input
                type="text"
                value={invitation.first_name}
                onChange={(e) => updateInvitation(index, 'first_name', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
                placeholder="First name"
              />
              <input
                type="text"
                value={invitation.last_name}
                onChange={(e) => updateInvitation(index, 'last_name', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
                placeholder="Last name"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <input
                  type="email"
                  value={invitation.email}
                  onChange={(e) => updateInvitation(index, 'email', e.target.value)}
                  className={`
                    w-full px-3 py-2 border rounded-lg text-sm
                    focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent
                    ${invitation.email && !isValidEmail(invitation.email)
                      ? 'border-red-300'
                      : 'border-gray-300'
                    }
                  `}
                  placeholder="email@example.com"
                />
              </div>
              <select
                value={invitation.role}
                onChange={(e) => updateInvitation(index, 'role', e.target.value as TeamInvitation['role'])}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
              >
                <option value="admin">Admin</option>
                <option value="advisor">Advisor</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
          </div>
        ))}
      </div>

      {/* Add more button */}
      {invitations.length < 10 && (
        <button
          type="button"
          onClick={addInvitation}
          className="w-full py-2 px-4 border-2 border-dashed border-gray-300 text-gray-500 rounded-lg hover:border-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center gap-2"
        >
          <Plus size={18} />
          Add another team member
        </button>
      )}

      {/* Role descriptions */}
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-xs font-medium text-gray-500 mb-2">Role Descriptions:</p>
        <ul className="space-y-1 text-xs text-gray-500">
          <li><strong>Admin</strong> - Full access to all features including user management</li>
          <li><strong>Advisor</strong> - Client management, meetings, portfolios, compliance</li>
          <li><strong>Viewer</strong> - Read-only access to clients and reports</li>
        </ul>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onSkip}
          disabled={isLoading || parentLoading}
          className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Skip & Complete
        </button>
        <button
          type="button"
          onClick={handleSendInvitations}
          disabled={isLoading || parentLoading || getValidInvitations().length === 0}
          className="flex-1 py-3 px-4 bg-kielis-navy text-white font-medium rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Mail size={18} />
              Send Invitations
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default InviteTeamStep;
